import { useEffect, useLayoutEffect } from 'react';
import { toast } from 'react-toastify';
import { errorMsg, userListToLabelEmailPhoneValue } from '../../../../../Apis/functions';
import {
    GetWorkShiftDetailById,
    UpdateWorkShiftFull
} from '../../../../../reduxToolkit/CompanyWorkShift/CompanyWorkShiftApi';
import { MODEL_DATA, STEPS_UPDATE_WORK_SHIFT } from '../Model/Models';
import { PersonChipBlack } from "../../../../../components/buttons/IconButtons";
import { permissionObj } from '../../../../../Helpers/permission';
import CommonWorkShiftApp from './CommonWorkShiftApp';

/**
 * Author: Arnulfo Ferro Zapata
 * Job Title: Fullstack
 * Email: azapata@ibl.mx
 *
 * Update-only hook. Loads the work shift detail into the local selection lists,
 * builds the update stepper labels and resubmits the name + resource ids. The
 * schedules section persists itself against the id (server), so it is not part of
 * this body. Mirrors ShiftAccess EditShiftAccessApp.
 */
export const UpdateWorkShiftApp = () => {

    const {
        DEV,
        permission,
        emptyFields,
        setEmptyFields,
        handleChange,
        selectDeleteSchedules, setSelectDeleteSchedules,
        selectDeleteDepartments, setSelectDeleteDepartments,
        selectDeleteUser, setSelectDeleteUser,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleOnBack,
        id,
        t,
        dispatch,
        item,
        setItem,
        setSteps,
        schedules, setSchedules,
        setUser,
        setDepartments,
        departments,
        user,
        searchElement,
        setSearchElement,
        modified, setModified
    } = CommonWorkShiftApp();

    // Users with read permission only reach this view as a detail: no edit action is offered
    const disabled = !DEV && !permission?.includes(permissionObj?.WEB_WORK_SHIFT_UPDATE);

    useLayoutEffect(() => {
        const stepArray = Object.values(STEPS_UPDATE_WORK_SHIFT)
            .filter(step => step?.TRANS && typeof step.INDEX === "number")
            .sort((a, b) => a.INDEX - b.INDEX)
            .map(step => t(step.TRANS));
        setSteps(stepArray);
    }, [t]);

    useEffect(() => {
        if (id) getData(true);
    }, [id]);

    useEffect(() => {
        if (modified && id) {
            // Reload the resource lists only; keep the local schedule buffer so the
            // user's pending schedule add/deletes survive resource-driven reloads.
            getData(false);
            setModified(false);
        }
    }, [modified]);

    /**
     * Flags server-loaded items as already assigned. They must stay in the
     * section list (toRegister source + assigned-tab delete), but the Add tab
     * must show only the newly added ones, so it filters this flag out.
     * @param {Array<Object>} list - Mapped section items.
     * @returns {Array<Object>}
     */
    const markAssigned = (list) => (list || []).map(e => ({ ...e, isAssigned: true }));

    /**
     * Loads the work shift detail by id and hydrates the form and the resource
     * selection lists (users / departments), flagging each as assigned. The
     * schedules buffer is seeded only on the initial load (hydrateSchedules);
     * later resource-driven reloads must not overwrite the user's pending edits.
     * Schedules are resubmitted on update so the server keeps the full set.
     * @param {boolean} [hydrateSchedules=false] - Seed the schedules buffer.
     */
    const getData = (hydrateSchedules = false) => {
        dispatch(GetWorkShiftDetailById(id)).then(({ payload: { data: { data } } }) => {
            setItem({ ...MODEL_DATA, id: data.id, name: data.name });
            if (hydrateSchedules) setSchedules(data.schedules || []);
            setUser(markAssigned(userListToLabelEmailPhoneValue(data.users, 'userId', true, PersonChipBlack)));
            setDepartments(markAssigned(userListToLabelEmailPhoneValue(data.departments, 'id', false, null)));
        });
    }

    /**
     * Validates the name and that at least one schedule exists, builds the update
     * body (name + resource ids) and updates it; on success notifies and triggers
     * a reload of the detail. Flags the empty fields so the name and the schedules
     * card render as required.
     */
    const handleAddEdit = () => {
        if (!item.isValid(schedules)) {
            setEmptyFields(true);
            toast.warn(t('please_fill_all_fields'));
            return;
        }
        setEmptyFields(false);
        const body = item.toRegister(schedules, user, departments);
        dispatch(UpdateWorkShiftFull(body)).then(({ payload: { data: { success, data, code } } }) => {
            if (success && data?.id) {
                toast.success(t('has_been_updated_successfully'));
                setModified(true);
            } else errorMsg(code);
        });
    }

    return {
        modified,
        setModified,
        id,
        DEV,
        permission,
        emptyFields,
        disabled,
        item,
        handleChange,

        schedules, setSchedules,
        selectDeleteSchedules, setSelectDeleteSchedules,

        departments, setDepartments,
        selectDeleteDepartments, setSelectDeleteDepartments,

        user, setUser,
        selectDeleteUser, setSelectDeleteUser,
        searchElement,
        setSearchElement,
        steps,
        activeStep, setActiveStep,
        handleStepChange,
        handleAddEdit,
        handleOnBack,
    }
}
